// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Description
//
// Include description of object & useful info here, please!

import { anythingToMidiNumbers } from './abstractInstrument';
import utils from '../utils';
import type { PlayOptions, MidiMessage, PlayMusicOptions } from './soundBufferHolder';
import SoundBufferHolder from './soundBufferHolder';
import type Note from './note';
import type Chord from './chord';
import type NoteHolder from './noteHolder';
import type MusicObject from './musicObject';

export default class PianoInstrument {
  buffer: SoundBufferHolder;

  context: false | AudioContext;

  options: {};

  static _activeBuffer: SoundBufferHolder;

  // Public constructor
  constructor() {
    this.context = utils.getAudioContext();
    if (this.context) this.initializeBuffer(this.context);
  }

  // eslint-disable-next-line class-methods-use-this
  isPianoInstrument(): this is PianoInstrument {
    return true;
  }

  /**
   * initialize the SoundBufferHolder() var buffer;
   * configure playback speeds & decay rates
   * load the sound files
   */
  private initializeBuffer(context: AudioContext) {
    if (typeof PianoInstrument._activeBuffer !== 'undefined') {
      this.buffer = PianoInstrument._activeBuffer;
      return;
    }
    this.buffer = new SoundBufferHolder(context);
    this.loadPianoSounds();
    this.loadMetronomeSounds();
    PianoInstrument._activeBuffer = this.buffer;
  }

  private loadPianoSounds() {
    const buffer = this.buffer;
    const sounds = {};
    let i;
    const decayRate = 0.07;
    // Configure playback speeds & decay rates for files to be loaded:
    for (i = 20; i < 110; i++) {
      /*  i+3 % = 5
      = 0: use original file
      = 1: use original file up a half step
      = 2: use original file up a whole step
      = 3: use next note up, down a whole step
      = 4: use next note up, down a half step
    */

      switch ((i + 3) % 5) {
        case 0:
          buffer.setPlaybackInfo(i, i, 1, decayRate);
          break;
        case 1:
          buffer.setPlaybackInfo(i, i - 1, 1.05946309436, decayRate);
          break;
        case 2:
          buffer.setPlaybackInfo(i, i - 2, 1.12246204831, decayRate);
          break;
        case 3:
          buffer.setPlaybackInfo(i, i + 2, 0.89089871814, decayRate);
          break;
        case 4:
          buffer.setPlaybackInfo(i, i + 1, 0.94387431268, decayRate);
          break;
      }
      // console.log("midinumber: " + i + " Buffer: " + buffer.playbackinfo[i]['buffer'] + "<br />");
    }

    // create a file list to load -- i+5 because we only load each fifth half step.
    for (i = 22; i < 110; i += 5) {
      sounds[i] = `/pianosounds/${i}`;
    }

    // Additionally, set metronome clicks:
    //      buffer.setPlaybackInfo(0, 0, 1, decayRate);
    //      buffer.setPlaybackInfo(1, 1, 1, decayRate);
    buffer.loadSounds(sounds);
  }

  private loadMetronomeSounds() {
    const buffer = this.buffer;
    const decayRate = 0.07;
    const sounds = {
      tock: '/metronome/lowclick',
      tick: '/metronome/highclick',
      silence: '/metronome/silence',
      // Silence-2 is VERY soft pink-noise, because Apple in their wisdom decided to filter
      // empty or truly silent waveforms from actually playing. Sigh. This allows us to
      // continue using the trick of playing a "silent" .001 of a second audio to give
      // realtime triggers off of the webaudio context timeline.
      'silence-2': '/metronome/silence-2'
    };
    // load the actual sounds
    buffer
      .loadSounds(sounds)
      .setPlaybackInfo('tick', 'tick', 1, decayRate)
      .setPlaybackInfo('tock', 'tock', 1, decayRate)
      .setPlaybackInfo('silence', 'silence', 1, decayRate)
      .setPlaybackInfo('silence-2', 'silence-2', 1, decayRate);
  }

  /**
   * Construct path to sound file from sound and instrument
   */
  private makeSoundFilename(sound: string, instrument = '') {
    instrument = `${instrument}/`;
    return `/${instrument}${sound}`;
  }

  loadTablaSounds() {
    const buffer = this.buffer;
    const decayRate = 0.0003;
    const soundnames = ['boh', 'dah', 'dun', 'duntick', 'tack', 'tack2'];
    const sounds = {};
    soundnames.forEach((sound) => {
      sounds[sound] = this.makeSoundFilename(sound, 'tabla');
      buffer.setPlaybackInfo(sound, sound, 1, decayRate, 'tabla');
    });

    buffer.setPlaybackInfo('dun0', 'dun', 1.007, decayRate, 'tabla');
    buffer.setPlaybackInfo('dun1', 'dun', 0.993, decayRate, 'tabla');
    buffer.setPlaybackInfo('dun2', 'dun', 1.0, decayRate, 'tabla');
    buffer.setPlaybackInfo('dah1', 'dah', 0.8, decayRate, 'tabla');

    buffer.loadSounds(sounds, 'tabla');
  }

  /** callback when load status has changed with a new percent (0 - 100) */
  notifyLoadStatus(callback: (number) => any, thisArg) {
    this.buffer.notifyLoadStatus(callback, thisArg);
  }

  /**
   * Play piano sounds
   */
  play(toPlay: number | string | Note | Chord | NoteHolder, playOptions?: PlayOptions) {
    const midiOrKeyArray = anythingToMidiNumbers(toPlay);
    midiOrKeyArray.forEach((midiNumberOrName) => {
      this.buffer.play(midiNumberOrName, playOptions);
    });
    return this;
  }

  playMusic(musicObject: MusicObject, playOptions: PlayMusicOptions = {}) {
    const { tempo, startTime } = playOptions;
    const midiMessages = musicObject.getMidiMessages();
    this.playMidiMessages(midiMessages, tempo || musicObject.tempo, startTime);
    return this;
  }

  playMidiMessages(midiMessages: MidiMessage[], tempo?: number, startTime?: number) {
    this.buffer.playMidiMessages(midiMessages, tempo, startTime);
    return this;
  }

  onStarted(callback: Function, thisArg: any, arg: any) {
    this.buffer.onStarted(callback, thisArg, arg);
  }

  onEnded(callback: Function, thisArg?: any, arg?: any) {
    this.buffer.onEnded(callback, thisArg, arg);
  }

  stop() {
    this.allNotesOff();
    return this;
  }

  noteOn(midiNumber: number | string | Note | Chord | NoteHolder, velocity: number) {
    const midiOrKeyArray = anythingToMidiNumbers(midiNumber);
    midiOrKeyArray.forEach((midiNumberOrName) => {
      this.buffer.noteOn(midiNumberOrName, velocity);
    });
    return this;
  }

  noteOff(midiNumber: number | string | Note | Chord | NoteHolder) {
    const midiOrKeyArray = anythingToMidiNumbers(midiNumber);
    midiOrKeyArray.forEach((midiNumberOrName) => {
      this.buffer.noteOff(midiNumberOrName);
    });
    return this;
  }

  allNotesOff() {
    this.buffer.panic();
    return this;
  }
}
